// FacetPanel — schema-driven sidebar rail. Renders the right facet primitive
// for each `FilterField` in the schema. Owns no state; the host passes a
// `state` and an `onChange(nextState)`.
//
// The host is responsible for asynchronous facet counts (passed in as
// `counts[fieldId]`) and for async-enum typeahead fetchers (passed as
// `asyncFetchers[fieldId]`). Keeping fetch out of this module makes it
// embeddable in any data layer.

import type { filters as f } from '@eidollon/contracts';
import { AsyncFacet } from './facets/AsyncFacet.js';
import { BooleanFacet } from './facets/BooleanFacet.js';
import { GeoPolygonFacet, type GeoPolygonFacetValue } from './facets/GeoPolygonFacet.js';
import type { FacetOption } from './facets/MultiSelectFacet.js';
import { MultiSelectFacet } from './facets/MultiSelectFacet.js';
import {
  RangeFacet,
  type RangeFacetValue,
  type RangeKind,
  type RangeRelativePreset,
} from './facets/RangeFacet.js';

export interface FacetPanelProps {
  schema: f.FilterSchema;
  state: f.FilterState;
  onChange: (next: f.FilterState) => void;
  /** Bucket counts per facet, keyed by field id. */
  counts?: Record<string, FacetOption[]>;
  /** Async typeahead fetchers, keyed by field id. */
  asyncFetchers?: Record<string, (q: string) => Promise<FacetOption[]>>;
  /** Resolves a code → display label for AsyncFacet selected chips. */
  resolveAsyncLabel?: Record<string, (value: string) => string | undefined>;
  /** Date-kind quick-pick presets, keyed by field id. */
  rangePresets?: Record<string, RangeRelativePreset[]>;
  /** geo-polygon editor handlers, keyed by field id (e.g. opens the map's draw mode). */
  geoEditors?: Record<string, () => void>;
  /** Host-provided translation of field labels (e.g. `STATUS` → `状态`). */
  formatFieldLabel?: (fieldId: string, fallback: string) => string;
  /** Host-provided translation of enum option values per field. */
  formatOptionLabel?: (fieldId: string, value: string) => string | undefined;
  /** Localized chrome labels passed through to facets (CLR, MORE ({{count}})). */
  facetChrome?: { clr?: string; more?: string };
}

export function FacetPanel({
  schema,
  state,
  onChange,
  counts,
  asyncFetchers,
  resolveAsyncLabel,
  rangePresets,
  geoEditors,
  formatFieldLabel,
  formatOptionLabel,
  facetChrome,
}: FacetPanelProps) {
  const labelOf = (id: string, fallback: string) =>
    formatFieldLabel ? formatFieldLabel(id, fallback) : fallback;
  const setField = (id: string, value: f.FilterValue | undefined) => {
    const next: f.FilterState = { ...state };
    if (
      value === undefined ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'string' && value === '')
    ) {
      delete next[id];
    } else {
      next[id] = value;
    }
    onChange(next);
  };

  return (
    <div data-component="FacetPanel">
      {schema.fields
        // Default is `facetable: true`; only explicit `false` opts out.
        .filter((field) => field.facetable !== false)
        .map((field) => renderField(field))}
    </div>
  );

  function renderField(field: f.FilterField): React.ReactNode {
    const v = state[field.id];

    switch (field.type) {
      case 'multi-enum':
      case 'enum': {
        const options: FacetOption[] =
          counts?.[field.id] ??
          field.pinned?.map((p) => ({ value: p.value, label: p.label })) ??
          [];
        const value: string[] =
          field.type === 'enum'
            ? typeof v === 'string' && v.length > 0
              ? [v]
              : []
            : Array.isArray(v)
              ? v.filter((x): x is string => typeof x === 'string')
              : [];
        return (
          <MultiSelectFacet
            key={field.id}
            field={field.id}
            label={labelOf(field.id, field.label)}
            options={options}
            value={value}
            formatOption={formatOptionLabel ? (v) => formatOptionLabel(field.id, v) : undefined}
            chrome={facetChrome}
            onChange={(next) => {
              if (field.type === 'enum') setField(field.id, next[0] ?? '');
              else setField(field.id, next);
            }}
          />
        );
      }
      case 'date-range':
      case 'money-range':
      case 'number-range': {
        const kind: RangeKind =
          field.type === 'date-range' ? 'date' : field.type === 'money-range' ? 'money' : 'number';
        const value: RangeFacetValue =
          v && typeof v === 'object' && !Array.isArray(v) ? (v as RangeFacetValue) : {};
        return (
          <RangeFacet
            key={field.id}
            field={field.id}
            label={labelOf(field.id, field.label)}
            kind={kind}
            value={value}
            relativePresets={rangePresets?.[field.id]}
            onChange={(next) => {
              const empty =
                next.from == null && next.to == null && next.min == null && next.max == null;
              setField(field.id, empty ? undefined : (next as unknown as f.FilterValue));
            }}
          />
        );
      }
      case 'bool':
        return (
          <BooleanFacet
            key={field.id}
            field={field.id}
            label={labelOf(field.id, field.label)}
            value={typeof v === 'boolean' ? v : undefined}
            onChange={(next) => setField(field.id, next)}
          />
        );
      case 'async-enum': {
        const fetcher = asyncFetchers?.[field.id];
        if (!fetcher) return null; // host hasn't wired this field yet; render nothing rather than a broken input
        const value: string[] = Array.isArray(v)
          ? v.filter((x): x is string => typeof x === 'string')
          : [];
        return (
          <AsyncFacet
            key={field.id}
            field={field.id}
            label={labelOf(field.id, field.label)}
            value={value}
            fetcher={fetcher}
            resolveLabel={resolveAsyncLabel?.[field.id]}
            onChange={(next) => setField(field.id, next)}
          />
        );
      }
      case 'geo-polygon': {
        const value: GeoPolygonFacetValue =
          v &&
          typeof v === 'object' &&
          !Array.isArray(v) &&
          Array.isArray((v as GeoPolygonFacetValue).polygons)
            ? (v as GeoPolygonFacetValue)
            : { polygons: [] };
        return (
          <GeoPolygonFacet
            key={field.id}
            field={field.id}
            label={labelOf(field.id, field.label)}
            value={value}
            onOpenEditor={geoEditors?.[field.id]}
            onChange={(next) => {
              const empty = next.polygons.length === 0;
              setField(field.id, empty ? undefined : (next as unknown as f.FilterValue));
            }}
          />
        );
      }
      default:
        return null;
    }
  }
}
